// src/components/Modal.js
//
// Modal ESTÁNDAR del sistema. TODO modal debe usar este componente (NUNCA overlays
// inline). El "marco" (overlay con blur, card con borde+sombra+radio, header con
// título + X roja, scroll fino, full-screen en móvil, History API) es idéntico para
// todos → tocar Modal.css/Modal.js cambia TODOS los modales a la vez. Cada modal solo
// difiere por su `children` (contenido) y su `size`/`maxWidth`.
//
// Props:
//   isOpen, onClose          — control de apertura.
//   title                    — título del header. Acepta string O JSX (ícono + texto).
//   size                     — 'small'|'normal'|'large'|'xlarge' (ancho preestablecido).
//   maxWidth                 — override de ancho (ej. '900px') para casos puntuales.
//   headerExtra              — JSX opcional bajo el título (navegación Anterior/Siguiente,
//                              subtítulos, stats, chips de estado, etc.).
//   closeOnOverlay (=true)   — si false, el click en el fondo NO cierra.
//   disableClose (=false)    — si true, oculta la X y bloquea el cierre (mientras procesa).
//   bodyClassName            — clase extra para el cuerpo (ej. para layouts de 2 columnas).
import React, { useRef, useEffect } from 'react';
import './Modal.css';
import { XMarkIcon } from './ui/Icons';

// Contador module-level: cada apertura recibe un id único → soporta modales ANIDADOS
// (uno abre otro). El back del teléfono cierra solo el de arriba, no ambos.
let modalCounter = 0;

function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'normal',
  maxWidth,
  headerExtra = null,
  closeOnOverlay = true,
  disableClose = false,
  bodyClassName = '',
  manageHistory = true,
  hideHeader = false,
  titleAlign = 'left'
}) {
  const overlayRef = useRef(null);
  const mouseDownOnOverlay = useRef(false);
  const idRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !manageHistory) return;
    const myId = ++modalCounter;
    idRef.current = myId;
    window.history.pushState({ modalId: myId }, '');
    const handlePopState = () => {
      // Cierro SOLO si volvimos a una entrada ANTERIOR a la mía (un modal ancestro o la tabla).
      // Comparar por "MENOR QUE" (no "distinto") es lo que hace que, con modales ANIDADOS, un
      // back de un nivel cierre solo el modal de arriba y NO los modales padres. Los ids son
      // incrementales por apertura, así que un ancestro siempre tiene id menor.
      if ((window.history.state?.modalId || 0) < myId) onClose();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Si nuestra entrada de historial sigue en el tope, la quitamos. Pasa cuando el
      // modal se cerró por Cancelar/Guardar (llaman onClose directo, sin history.back).
      // Si se cerró por la X o el botón atrás, el state ya cambió y este back() se omite.
      //
      // El back() se DIFIERE un microtask a propósito: en el DOBLE-MONTAJE de StrictMode
      // (solo en desarrollo) React hace montar → desmontar → montar de corrido, y el
      // re-montaje empuja su PROPIA entrada (modalId mayor) justo después de este cleanup.
      // Al correr el microtask, el modalId vigente ya NO es el nuestro → se omite el back(),
      // evitando ese popstate espurio que cerraba el modal recién montado (parpadeo en dev).
      // En un cierre real no hay re-montaje: el id sigue siendo el nuestro y el back() limpia
      // la entrada igual que antes.
      Promise.resolve().then(() => {
        if (window.history.state?.modalId === myId) window.history.back();
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, manageHistory]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (disableClose) return;
    // Si manageHistory es false, el componente padre maneja el History (pushState/back).
    // Útil mientras conviven modales nuevos (<Modal>) con modales inline viejos del mismo módulo.
    if (manageHistory && window.history.state?.modalId === idRef.current) {
      window.history.back(); // dispara popstate → onClose
    } else {
      onClose();
    }
  };

  const handleMouseDown = (e) => {
    mouseDownOnOverlay.current = e.target === overlayRef.current;
  };

  const handleMouseUp = (e) => {
    if (closeOnOverlay && !disableClose && e.target === overlayRef.current && mouseDownOnOverlay.current) {
      handleClose();
    }
    mouseDownOnOverlay.current = false;
  };

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div
        className={`modal-content modal-${size}`}
        style={maxWidth ? { width: maxWidth, maxWidth } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* hideHeader: el contenido (children) trae su propio header/X (ej. CrearPedidoShopify).
            El Modal solo aporta marco + History. */}
        {!hideHeader && (
          <div className={`modal-header ${titleAlign === 'center' ? 'modal-header-center' : ''}`}>
            <h3>{title}</h3>
            {!disableClose && (
              <button className="modal-close" onClick={handleClose} title="Cerrar"><XMarkIcon className="w-5 h-5" /></button>
            )}
          </div>
        )}
        {headerExtra && <div className="modal-header-extra">{headerExtra}</div>}
        <div className={`modal-body ${hideHeader ? 'modal-body-flush' : ''} ${bodyClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default Modal;